/**
 * CLIENT CONFIGURATION
 * ====================
 * Fire Department of Bellevue-Dayton
 *
 * INSTRUCTIONS:
 * 1. Copy the entire app folder for a new client
 * 2. Update values in this file
 * 3. Replace logo files in /src/assets/images/
 * 4. Update /public/manifest.json to match branding
 * 5. Create new Supabase project and update credentials
 * 6. Run SQL schema to create tables
 */

// ============================================
// BRANDING
// ============================================
export const BRANDING = {
  // Organization Identity
  companyName: 'Bellevue-Dayton',
  companyFullName: 'Fire Department of Bellevue-Dayton',

  // App Identity
  appName: 'NarcoticTrack',
  appTagline: 'the most efficient and safe way to inventory controlled substances in EMS.',
  copyright: 'NarcoticTrack Inventory System, LLC',
  copyrightYear: '2025',

  // Theme Colors - Professional navy/slate with clean white
  primaryColor: '#1E3A5F',           // Deep navy (from logo)
  accentColor: '#D4AF37',            // Classic gold
  secondaryColor: '#2C5282',         // Rich blue
  backgroundColor: '#F7FAFC',        // Clean off-white
  backgroundGradient: 'linear-gradient(180deg, #F7FAFC 0%, #EDF2F7 100%)',
  cardBackground: '#FFFFFF',         // Pure white cards
  textColor: '#1A202C',              // Near black text
  textOnCard: '#1A202C',             // Dark text on white cards
  textMuted: '#718096',              // Gray muted text
  borderColor: '#E2E8F0',            // Light gray borders
  buttonColor: '#1E3A5F',            // Navy buttons
  buttonTextColor: '#FFFFFF',        // White text on buttons

  // PWA Manifest
  pwaShortName: 'FDBD NT',
  pwaFullName: 'Fire Department of Bellevue-Dayton NarcoticTrack',
  themeColor: '#4A6FA5',

  // Logos (filenames in /src/assets/images/)
  logoFilename: 'FDBDLogo.png',
  ntLogoFilename: 'NTlogo.jpeg',
};

// ============================================
// SUPABASE CONFIGURATION
// ============================================
export const SUPABASE = {
  url: 'https://ygkijbrlgtymafnchxyp.supabase.co',
  anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inlna2lqYnJsZ3R5bWFmbmNoeHlwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc0NzgxNDksImV4cCI6MjA4MzA1NDE0OX0.1GSgo8SXHh8ngmYm74Uun580TkxpO6h817SjZosxSZw',
};

// ============================================
// LOCATIONS
// ============================================
export const LOCATIONS = [
  { id: 'medic-280', name: 'Medic 280', type: 'medic', tableName: 'medic_280' },
  { id: 'station-med-vault', name: 'Station Med Vault', type: 'station', tableName: 'station_med_vault' },
];

// Helper: Get locations by type
export const getLocationsByType = (type) => LOCATIONS.filter(loc => loc.type === type);
export const getLocationById = (id) => LOCATIONS.find(loc => loc.id === id);

// ============================================
// MEDICATIONS
// ============================================
export const MEDICATIONS = [
  { id: 'fentanyl', displayName: 'Fentanyl', schedule: 'II' },
  { id: 'ketamine', displayName: 'Ketamine', schedule: 'III' },
  { id: 'morphine', displayName: 'Morphine', schedule: 'II' },
  { id: 'versed', displayName: 'Versed', schedule: 'IV' },
];

// Get medication IDs as array (for DB queries)
export const MEDICATION_IDS = MEDICATIONS.map(m => m.id);

// Format medication name for display
export const formatMedicationName = (id) => {
  const med = MEDICATIONS.find(m => m.id === id);
  return med ? med.displayName : id;
};

// Get medications for a specific location
// Returns { sections: [{ title, medications }] } format
export const getLocationMedications = (locationId) => {
  // All locations use the same medications (no multi-section locations)
  return {
    sections: [
      {
        title: null, // No section title needed
        medications: MEDICATIONS
      }
    ]
  };
};

// ============================================
// SEAL/TAG TRACKING
// ============================================
export const SEAL_CONFIG = {
  // Medic has tag tracking
  medic: {
    enabled: true,
    fields: [
      { id: 'old_tag', displayName: 'Old Tag', required: false },
      { id: 'new_tag', displayName: 'New/Current Tag', required: false },
    ],
  },
  // Station has tag tracking
  station: {
    enabled: true,
    fields: [
      { id: 'old_tag', displayName: 'Old Tag', required: false },
      { id: 'new_tag', displayName: 'New/Current Tag', required: false },
    ],
  },
};

// Get seal fields for a location type
export const getSealFields = (locationType) => {
  return SEAL_CONFIG[locationType]?.fields || [];
};

// ============================================
// UNIT IN-SERVICE CONFIGURATION
// ============================================
export const UNIT_IN_SERVICE_CONFIG = {
  enabled: true,
  storageOptions: [
    { value: '', label: 'Select a location' },
    { value: 'Out of Service Ambulance', label: 'Out of Service Ambulance' },
    { value: 'Station Storage', label: 'Station Storage' },
    { value: 'Another In-Service', label: 'Another In-Service' },
  ],
  storageNote: 'If "Another In-Service" option is selected, please add unit # in the additional notes section',
};

// ============================================
// FORM REQUIREMENTS
// ============================================
export const FORM_CONFIG = {
  // Signature Requirements
  requirePrimarySignature: true,
  requireSecondarySignature: true,      // Required for Daily Inventory Check
  requireSecondaryPinValidation: true,

  // Secondary Signature uses State License Number + PIN
  secondaryValidationMethod: 'license_and_pin',  // 'pin_only' or 'license_and_pin'

  // Field Requirements
  requireInventoryPhoto: false,
  requireRunNumber: false,               // Only shown for Patient Use/Restock
  requireReasonForChange: true,
  requireAdditionalNotes: false,

  // Field Visibility
  showRunNumber: true,                   // Shown only when reason is 'Patient Use/Restock'
  showAdditionalNotes: true,
  showInventoryPhoto: true,
  showUnitInService: true,               // Unit In-Service Yes/No question

  // PIN validation rules
  pinMinLength: 4,
  pinMaxLength: 6,

  // Session timeout (milliseconds)
  sessionTimeout: 5 * 60 * 1000,

  // Reason for change options
  reasonOptions: [
    'Daily Inventory Check',
    'Expiration/Restock',
    'Patient Use/Restock',
  ],

  // Which reason requires secondary signature
  reasonsRequiringSecondarySignature: ['Daily Inventory Check'],

  // Which reason shows run number
  reasonsShowingRunNumber: ['Patient Use/Restock'],
};

// ============================================
// FEATURE FLAGS
// ============================================
export const FEATURES = {
  // ---- DASHBOARDS ----
  dailyDashboard: true,
  adminDashboard: true,

  // ---- REPORTS (Admin Only) ----
  chainOfCustodyReport: true,
  providerUsageReport: true,
  masterInventory: false,
  expirationReport: false,
  mileageReport: false,

  // ---- MEDICAL CALCULATORS ----
  patientCareGuidelines: false,
  weightConverter: false,
  ivCalculator: false,
  glasgowComaScale: false,
  apgarCalculator: false,
  oxygenDuration: false,
  phoneDirectory: false,

  // ---- MEDICATION TRACKING ----
  expirationTracking: false,
  expirationAlertDays: 30,

  // ---- DEA COMPLIANCE ----
  deaForm41: true,

  // ---- AI COMPLIANCE INSIGHTS ----
  aiInsights: true,

  // ---- EDITOR ROLE ----
  requireEditorRole: true,  // Only users with editor='x' can modify inventory
};

// ============================================
// ADMIN FEATURES
// ============================================
export const ADMIN_FEATURES = [
  { id: 'users', name: 'User List', path: '/users', enabled: true },
  { id: 'provider-usage', name: 'Provider Usage Report', path: '/provider-usage-report', enabled: true },
  { id: 'chain-of-custody', name: 'Chain of Custody Report', path: '/chain-of-custody-report', enabled: true },
  { id: 'daily-dashboard', name: 'Daily Dashboard', path: '/daily-dashboard', enabled: true },
  { id: 'ai-insights', name: 'AI Compliance Insights', path: '/ai-insights', enabled: true },
];

// ============================================
// LOCATION IDENTIFIER
// ============================================
export const CLIENT_ID = 'bellevue-dayton';

// ============================================
// EXPORT ALL CONFIG
// ============================================
const CONFIG = {
  BRANDING,
  SUPABASE,
  LOCATIONS,
  MEDICATIONS,
  MEDICATION_IDS,
  SEAL_CONFIG,
  UNIT_IN_SERVICE_CONFIG,
  FORM_CONFIG,
  FEATURES,
  ADMIN_FEATURES,
  CLIENT_ID,

  // Helper functions
  getLocationsByType,
  getLocationById,
  formatMedicationName,
  getLocationMedications,
  getSealFields,
};

export default CONFIG;
